import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import styled from "styled-components";
import { Row, Col, Card, Spinner } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faDownload } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import FiscalAdress from "./FiscalAdress";
import ActiveSubscription from "../components/ActiveSubscription";
import { listInvoices } from "../actions/paymentActions";
import PaymentMethodManager from "../components/PaymentMethodManager";
import CurrentPayMethodChip from "../components/CurrentPayMethodChip";

import {
  Container,
  CardHeader,
  CardHeaderTitle,
  SettingsCard,
  SectionTitle,
} from "../components/DefaultImports";
import { useParams } from "react-router-dom";
import { fetchRestaurantDetails } from "../actions/restaurantActions";

/* ---------- Search styles ---------- */
const SearchContainer = styled.div`
  max-width: 500px;
  margin: 10px auto 12px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 44px 12px 16px;
  border-radius: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  font-size: 15px;

  &:focus {
    outline: none;
    border-color: #ee754c;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.15);
  }
`;

const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
`;

/* ---------- Invoice card ---------- */
const InvoiceCard = styled(Card)`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.07);
  }
`;

const ReceiptButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: none;
  border-radius: 999px;
  padding: 8px 14px;
  min-height: 36px;
  background: ${(p) => (p.disabled ? "#eee" : "#fff1ec")};
  color: ${(p) => (p.disabled ? "#aaa" : "#ee754c")};
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;

  &:hover {
    background: ${(p) => (p.disabled ? "#eee" : "#ffe3d8")};
  }
`;

const ActionArea = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: 767px) {
    justify-content: flex-start;
    margin-top: 8px;
  }
`;

/* ---------- Status badge ---------- */
const Badge = styled.span`
  background: ${(p) => p.bg || "#ccc"};
  color: #fff;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
`;

const SmallMuted = styled.span`
  color: #777;
  font-size: 13px;
`;

/* ---------- Scrollable list area ---------- */
const LIST_BOTTOM_PADDING = 40;
const MIN_LIST_HEIGHT = 220;

const InvoiceListWrapper = styled.div`
  height: ${(p) => `${p.$height}px`};
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 12px 4px 2px;
  border-radius: 8px;
  scrollbar-gutter: stable;
`;

/* ---------- Pagination ---------- */
const Pager = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 12px 0 14px;
`;

const PagerButton = styled.button`
  background: #f2f2f2;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  min-width: 90px;
  text-align: center;

  &:hover {
    background: #e0e0e0;
  }
`;

const PagerButtonGhost = styled(PagerButton)`
  visibility: hidden;
`;

const PageNumber = styled.div`
  background: #ee754c;
  color: #fff;
  padding: 6px 14px;
  border-radius: 20px;
  min-width: 40px;
  text-align: center;
`;

/* ---------- helpers ---------- */
const centsToEUR = (cents, currency = "eur") =>
  `${(Number(cents || 0) / 100).toFixed(2)} ${String(
    currency || "EUR",
  ).toUpperCase()}`;

const moloniStatusToBadge = (inv) => {
  const moloni = inv?.moloni;

  if (moloni?.download_available || moloni?.pdf_url) {
    return { label: "Recibo disponível", bg: "#28a745" };
  }

  if (moloni?.status === "created") {
    return { label: "Criado", bg: "#6c757d" };
  }

  if (moloni?.status === "failed") {
    return { label: "Erro na faturação", bg: "#ff704d" };
  }

  if (inv?.status === "paid") {
    return { label: "A processar", bg: "#17a2b8" };
  }

  switch (inv?.status) {
    case "open":
      return { label: "Aberta", bg: "#17a2b8" };
    case "draft":
      return { label: "Rascunho", bg: "#6c757d" };
    case "uncollectible":
      return { label: "Incobrável", bg: "#ffc107" };
    case "void":
      return { label: "Anulada", bg: "#ff704d" };
    default:
      return { label: inv?.status || "—", bg: "#999" };
  }
};

const getDisplayNumber = (inv) => {
  return (
    inv?.moloni?.document_number ||
    inv?.moloni?.document_id ||
    inv?.number ||
    inv?.id ||
    "—"
  );
};

const getMoloniReceiptUrl = (inv) => {
  return inv?.moloni?.pdf_url || inv?.moloni?.invoice_receipt_pdf_url || null;
};

export default function SubscriptionManagement() {
  const params = useParams();
  const dispatch = useDispatch();

  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails;

  const invoicesState = useSelector((s) => s.payments);
  const { invoices = [], isLoadingInvoices } = invoicesState;

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [listHeight, setListHeight] = useState(0);
  const listRef = useRef(null);
  const pageRef = useRef(null);

  useEffect(() => {
    if (params.restaurant_id) {
      dispatch(fetchRestaurantDetails(params.restaurant_id));
      dispatch(listInvoices({ restaurantId: params.restaurant_id }));
    }
  }, [params.restaurant_id, dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 400);

    return () => clearTimeout(t);
  }, [searchTerm]);

  useLayoutEffect(() => {
    const updateListHeight = () => {
      if (!listRef.current || !pageRef.current) return;

      const pageRect = pageRef.current.getBoundingClientRect();
      const listRect = listRef.current.getBoundingClientRect();
      const available = pageRect.bottom - listRect.top - LIST_BOTTOM_PADDING;

      setListHeight(Math.max(MIN_LIST_HEIGHT, available));
    };

    updateListHeight();

    const onResize = () => updateListHeight();

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    const ro = new ResizeObserver(updateListHeight);
    ro.observe(document.body);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      ro.disconnect();
    };
  }, [isLoadingInvoices, debouncedSearchTerm, currentPage]);

  const filtered = invoices
    .filter((inv) => {
      const q = debouncedSearchTerm.toLowerCase();

      return (
        String(inv?.number || "")
          .toLowerCase()
          .includes(q) ||
        String(inv?.id || "")
          .toLowerCase()
          .includes(q) ||
        String(inv?.moloni?.document_number || "")
          .toLowerCase()
          .includes(q) ||
        String(inv?.moloni?.document_id || "")
          .toLowerCase()
          .includes(q)
      );
    })
    .sort((a, b) => (b.created || 0) - (a.created || 0));

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const results = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <Container ref={pageRef}>
      <ToastContainer />

      <CardHeader>
        <CardHeaderTitle>Gerir plano</CardHeaderTitle>
      </CardHeader>

      <Row className="g-3 align-items-stretch">
        <Col lg={5} xl={5}>
          <SettingsCard style={{ height: "100%" }}>
            <ActiveSubscription restaurant_id={restaurants?.id} />
          </SettingsCard>
        </Col>

        <Col lg={7} xl={7}>
          <SettingsCard style={{ marginBottom: 10 }}>
            <SectionTitle>Método de pagamento</SectionTitle>

            <div style={{ marginBottom: 10 }}>
              <CurrentPayMethodChip restaurantId={restaurants?.id} />
            </div>

            <PaymentMethodManager restaurantId={restaurants?.id} />
          </SettingsCard>

          <SettingsCard>
            <SectionTitle>Endereço fiscal</SectionTitle>

            <FiscalAdress
              showTitle={false}
              cardsPerRow={1}
              variant="simple"
              fiscal_adress_id={restaurants?.fiscal_information}
              is_payment_management_panel={true}
            />
          </SettingsCard>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xs={12}>
          <SettingsCard>
            <SectionTitle>Recibos</SectionTitle>

            <SearchContainer>
              <SearchInput
                placeholder="Procurar por nº de recibo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <SearchIcon icon={faSearch} />
            </SearchContainer>

            <Pager>
              {currentPage > 1 ? (
                <PagerButton onClick={() => setCurrentPage((p) => p - 1)}>
                  ← Anterior
                </PagerButton>
              ) : (
                <PagerButtonGhost>← Anterior</PagerButtonGhost>
              )}

              <PageNumber>{currentPage}</PageNumber>

              {currentPage < totalPages ? (
                <PagerButton onClick={() => setCurrentPage((p) => p + 1)}>
                  Seguinte →
                </PagerButton>
              ) : (
                <PagerButtonGhost>Seguinte →</PagerButtonGhost>
              )}
            </Pager>

            {isLoadingInvoices ? (
              <div className="text-center my-3">
                <Spinner animation="border" />
                <p>A carregar recibos...</p>
              </div>
            ) : (
              <InvoiceListWrapper ref={listRef} $height={listHeight}>
                {results.map((inv) => {
                  const when = inv.created
                    ? new Date(inv.created * 1000)
                    : null;

                  const { label, bg } = moloniStatusToBadge(inv);

                  const total =
                    inv.amount_paid > 0 ? inv.amount_paid : inv.amount_due;

                  const receiptUrl = getMoloniReceiptUrl(inv);
                  const canDownloadReceipt = Boolean(receiptUrl);

                  return (
                    <InvoiceCard key={inv.id}>
                      <Row className="align-items-center">
                        <Col md={4} xs={12} className="mb-2 mb-md-0">
                          <div>
                            <strong>{getDisplayNumber(inv)}</strong>

                            <span
                              className="text-muted"
                              style={{ marginLeft: 6 }}
                            >
                              {when ? when.toLocaleDateString() : ""}
                            </span>

                            {inv?.moloni?.error_message ? (
                              <div>
                                <SmallMuted>
                                  Erro: {inv.moloni.error_message}
                                </SmallMuted>
                              </div>
                            ) : null}
                          </div>
                        </Col>

                        <Col md={2} xs={12} className="mb-2 mb-md-0">
                          <strong>{centsToEUR(total, inv.currency)}</strong>
                        </Col>

                        <Col md={3} xs={12} className="mb-2 mb-md-0">
                          <Badge bg={bg}>{label}</Badge>
                        </Col>

                        <Col md={3} xs={12}>
                          <ActionArea>
                            <ReceiptButton
                              type="button"
                              disabled={!canDownloadReceipt}
                              onClick={() => {
                                if (!canDownloadReceipt) return;
                                window.open(receiptUrl, "_blank");
                              }}
                              title={
                                canDownloadReceipt
                                  ? "Download recibo"
                                  : "Recibo ainda não disponível"
                              }
                            >
                              <FontAwesomeIcon icon={faDownload} />
                              <span>Fatura/Recibo</span>
                            </ReceiptButton>
                          </ActionArea>
                        </Col>
                      </Row>
                    </InvoiceCard>
                  );
                })}

                {!results.length && (
                  <p className="text-center mt-3">Nenhum recibo encontrado.</p>
                )}
              </InvoiceListWrapper>
            )}
          </SettingsCard>
        </Col>
      </Row>
    </Container>
  );
}
